import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { toast } from "sonner";
import {
  Upload, Activity, Layers, Sparkles, Scissors, SlidersHorizontal,
  Zap, Gauge, Download, FileText, History, MonitorSmartphone, WifiOff,
  ArrowRight, Check, Music4, Monitor, Apple, Chrome, Globe,
} from "lucide-react";
import { api, formatApiError, trackEvent } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Logo } from "../components/Logo";
import { AiChatWidget } from "../components/AiChat";
import { DESKTOP_WIN_URL, HERO_IMAGE_URL } from "../lib/constants";
import { LANGS } from "../lib/translations";

const MARQUEE = [
  "PHOTO → VIDÉO", "fal.ai KLING", "LIP-SYNC", "9:16 · 16:9 · 1:1", "MODE AUTO",
  "TIMELINE", "EXPORT MP4", "FFMPEG", "SCÈNES IA", "MUSIQUE INTACTE",
];

const BENTO_META = [
  { icon: Upload, k: 0, span: "md:col-span-2 md:row-span-2", tone: "orange" },
  { icon: Activity, k: 1, span: "md:col-span-2" },
  { icon: Layers, k: 2, span: "md:col-span-2" },
  { icon: Scissors, k: 3, span: "md:col-span-3" },
  { icon: Gauge, k: 4, span: "md:col-span-3" },
];

const STRIP_ICONS = [SlidersHorizontal, Zap, Sparkles, Download, FileText, History, MonitorSmartphone, WifiOff];

const PRICING_META = [
  { name: "Draft", k: "draft", price: null, lookup: null },
  { name: "Standard", k: "standard", price: "15€", period: true, lookup: "standard_monthly" },
  { name: "High / Studio", k: "studio", price: "25€", period: true, lookup: "studio_monthly", popular: true },
  { name: "Master Pro", k: "master", price: "150€", lifetime: true, lookup: "master_lifetime" },
];

function WaveRibbon() {
  const bars = Array.from({ length: 64 });
  return (
    <div className="flex items-end gap-[3px] h-24 sm:h-32" aria-hidden>
      {bars.map((_, i) => (
        <motion.span key={i}
          className="w-[4px] sm:w-[6px] rounded-full"
          style={{ background: i % 3 === 0 ? "#FF5500" : i % 3 === 1 ? "#6366F1" : "#0F172A" }}
          initial={{ height: 6 }}
          animate={{ height: [6, 10 + Math.abs(Math.sin(i * 0.6)) * 90 + Math.random() * 20, 6] }}
          transition={{ duration: 1.1 + (i % 5) * 0.12, repeat: Infinity, ease: "easeInOut", delay: i * 0.02 }}
        />
      ))}
    </div>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const heroRef = useRef(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    trackEvent("pageview", "landing", "/");
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", { email });
      trackEvent("waitlist", email);
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const checkout = async (tier) => {
    if (!tier.lookup) { navigate("/studio"); return; }
    trackEvent("checkout", tier.name);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: tier.lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-[#FAF8F5] text-[#0F172A] overflow-x-hidden">
      {/* NAV */}
      <nav className="fixed top-4 inset-x-0 z-50 flex justify-center px-4">
        <div className="w-full max-w-6xl flex items-center justify-between backdrop-blur-xl bg-white/70 border border-[#E2E8F0] rounded-2xl px-4 sm:px-6 py-3 shadow-sm">
          <button onClick={() => scrollTo("top")} className="flex items-center"><Logo className="h-8 w-8" withText textClass="text-lg" /></button>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-[#475569]">
            <button onClick={() => scrollTo("features")} className="hover:text-[#FF5500] transition-colors">{t("nav.features")}</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-[#FF5500] transition-colors">{t("nav.pricing")}</button>
            <button onClick={() => scrollTo("install")} className="hover:text-[#FF5500] transition-colors">{t("nav.install")}</button>
            <button onClick={() => scrollTo("waitlist")} className="hover:text-[#FF5500] transition-colors">{t("nav.waitlist")}</button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 mr-1 rounded-full border border-[#E2E8F0] p-0.5" data-testid="lang-switcher">
              <Globe className="h-3.5 w-3.5 text-[#94A3B8] mx-1" />
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                  className={`text-xs font-semibold rounded-full px-2 py-1 transition-colors ${lang === l.code ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-slate-100"}`}>
                  {l.flag}
                </button>
              ))}
            </div>
            {user ? (
              <>
                {user.role === "admin" && <button onClick={() => navigate("/admin")} data-testid="nav-admin-button" className="hidden sm:inline text-sm px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors">{t("nav.admin")}</button>}
                <button onClick={() => navigate("/account")} data-testid="nav-account-button" className="hidden sm:inline text-sm px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors">{t("nav.account")}</button>
                <button onClick={() => navigate("/studio")} data-testid="nav-studio-button" className="text-sm px-4 py-2 rounded-xl bg-[#0F172A] text-white hover:bg-[#FF5500] transition-colors">{t("nav.studio")}</button>
                <button onClick={logout} className="hidden sm:inline text-sm text-[#94A3B8] hover:text-[#0F172A] transition-colors px-2">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate("/login")} data-testid="nav-login-button" className="text-sm px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors">{t("nav.login")}</button>
                <button onClick={() => navigate("/studio")} data-testid="nav-try-button" className="text-sm px-4 py-2 rounded-xl bg-[#0F172A] text-white hover:bg-[#FF5500] transition-colors">{t("nav.try")}</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header id="top" ref={heroRef} className="relative min-h-screen flex flex-col justify-center pt-28 pb-12 px-4 sm:px-8">
        <div className="absolute inset-0 pointer-events-none opacity-100" style={{ background: "radial-gradient(circle at 82% 12%, rgba(255,85,0,0.16), transparent 42%), radial-gradient(circle at 8% 88%, rgba(99,102,241,0.16), transparent 44%), radial-gradient(circle at 50% 50%, rgba(168,85,247,0.06), transparent 60%)" }} />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative max-w-6xl mx-auto w-full grid lg:grid-cols-2 lg:grid-rows-[auto_1fr] gap-x-10 lg:gap-x-14 gap-y-8 items-start">
          {/* Badge + title */}
          <div className="lg:col-start-1 lg:row-start-1">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white border border-[#E2E8F0] rounded-full px-4 py-1.5 mb-8">
                <span className="h-2 w-2 rounded-full bg-[#FF5500] animate-pulse" /> {t("hero.badge")}
              </div>
            </Reveal>
            <h1 className="font-heading font-extrabold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-[4.5rem]">
              {[t("hero.l1"), t("hero.l2"), t("hero.l3")].map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span className="block"
                    initial={{ y: "110%" }} animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}>
                    {i === 1 ? <span className="text-[#FF5500]">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>

          {/* Hero photo — faces the title (right on desktop, right after title on mobile) */}
          <Reveal delay={0.15} className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center">
            <div className="relative">
              <div className="relative rounded-[2rem] overflow-hidden border border-[#E2E8F0] shadow-2xl bg-[#0B0D12] aspect-[16/10] sm:aspect-[4/3] lg:aspect-[3/4] max-w-md mx-auto">
                <img src={HERO_IMAGE_URL} alt={t("hero.imgAlt")} className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(11,13,18,0.35))" }} />
              </div>
              <div className="absolute -bottom-4 -left-2 sm:-left-4 bg-white border border-[#E2E8F0] rounded-2xl px-4 py-3 shadow-lg flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#FF5500] animate-pulse" />
                <span className="font-mono text-xs">Scènes IA · Lip-sync · Export MP4</span>
              </div>
            </div>
          </Reveal>

          {/* Description + CTAs + demo */}
          <div className="lg:col-start-1 lg:row-start-2">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }}
              className="max-w-xl text-lg text-[#475569]">
              {t("hero.descA")}<strong className="text-[#0F172A]">{t("hero.descBold")}</strong>{t("hero.descB")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-3">
              <button onClick={() => navigate("/studio")} data-testid="hero-studio-button"
                className="group inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold rounded-2xl px-7 py-4 transition-colors">
                {t("hero.ctaStudio")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => scrollTo("waitlist")} data-testid="hero-waitlist-button"
                className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] hover:border-[#0F172A] font-semibold rounded-2xl px-7 py-4 transition-colors">
                {t("hero.ctaWaitlist")}
              </button>
            </motion.div>
          </div>
        </motion.div>
      </header>

      {/* MARQUEE */}
      <div className="border-y border-[#E2E8F0] bg-white py-5 overflow-hidden">
        <div className="flex whitespace-nowrap tx-marquee">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-6 font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] inline-flex items-center gap-6">
              {m} <Music4 className="h-4 w-4 text-[#FF5500]" />
            </span>
          ))}
        </div>
      </div>

      {/* BENTO */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight max-w-xl leading-tight">{t("bento.title")}</h2>
            <p className="text-[#475569] max-w-sm">{t("bento.subtitle")}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[minmax(150px,auto)] gap-4 sm:gap-5">
          {BENTO_META.map((b, i) => (
            <Reveal key={b.k} delay={i * 0.05} className={b.span}>
              <div className={`h-full rounded-3xl p-7 border transition-all hover:-translate-y-1 ${b.tone === "orange" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                <b.icon className={`h-7 w-7 mb-5 ${b.tone === "orange" ? "text-[#FF5500]" : "text-[#0F172A]"}`} />
                <h3 className="font-heading text-xl sm:text-2xl font-bold mb-2">{t(`bento.i${b.k}t`)}</h3>
                <p className={`text-sm leading-relaxed ${b.tone === "orange" ? "text-slate-300" : "text-[#475569]"}`}>{t(`bento.i${b.k}d`)}</p>
                {b.tone === "orange" && <div className="mt-6"><WaveRibbon /></div>}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          {STRIP_ICONS.map((Icon, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div className="flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl px-4 py-4 h-full hover:border-[#FF5500] transition-colors">
                <Icon className="h-5 w-5 text-[#FF5500] shrink-0" />
                <span className="text-sm font-medium">{t("strip")[i]}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-8">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-6 items-stretch">
            <div className="relative rounded-3xl overflow-hidden min-h-[340px] bg-[#0B0D12]">
              <img src="https://images.unsplash.com/photo-1618609377864-68609b857e90?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Console de mixage TUBMIX" className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(11,13,18,0.9))" }} />
              <div className="absolute bottom-0 p-8 text-white">
                <span className="font-mono text-xs uppercase tracking-widest text-[#FF5500]">{t("showcase.consoleTag")}</span>
                <h3 className="font-heading text-2xl font-bold mt-2">{t("showcase.consoleTitle")}</h3>
              </div>
            </div>
            <div className="rounded-3xl p-8 sm:p-10 bg-white border border-[#E2E8F0] flex flex-col justify-center">
              <h3 className="font-heading text-3xl font-extrabold leading-tight mb-4">{t("showcase.aiTitle1")}<br />{t("showcase.aiTitle2")}</h3>
              <p className="text-[#475569] mb-6">{t("showcase.aiDesc")}</p>
              <div className="space-y-3">
                {t("showcase.points").map((p) => (
                  <div key={p} className="flex items-center gap-3"><Check className="h-5 w-5 text-[#FF5500]" /><span className="text-sm font-medium">{p}</span></div>
                ))}
              </div>
              <button onClick={() => navigate("/studio")} className="mt-8 self-start inline-flex items-center gap-2 font-semibold text-[#FF5500] hover:gap-3 transition-all">{t("showcase.test")} <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-mono text-xs uppercase tracking-widest text-[#FF5500]">{t("pricing.tag")}</span>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight mt-3">{t("pricing.title")}</h2>
            <p className="text-[#475569] mt-4">{t("pricing.subtitle")}</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRICING_META.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.06}>
              <div className={`h-full rounded-3xl p-7 border flex flex-col ${tier.popular ? "bg-[#0F172A] text-white border-[#0F172A] shadow-xl scale-[1.02]" : "bg-white border-[#E2E8F0]"}`}>
                {tier.popular && <span className="self-start text-xs font-mono uppercase tracking-widest bg-[#FF5500] text-white rounded-full px-3 py-1 mb-4">{t("pricing.popular")}</span>}
                <h3 className="font-heading text-xl font-bold">{tier.name}</h3>
                <p className={`text-xs font-mono mt-1 ${tier.popular ? "text-slate-400" : "text-[#94A3B8]"}`}>{t(`pricing.q.${tier.k}`)}</p>
                <div className="mt-5 mb-6 flex items-end gap-1">
                  <span className="font-heading text-4xl font-extrabold">{tier.price || t("pricing.free")}</span>
                  {tier.period && <span className={`text-sm mb-1 ${tier.popular ? "text-slate-400" : "text-[#94A3B8]"}`}>{t("pricing.perMonth")}</span>}
                  {tier.lifetime && <span className={`text-sm mb-1 ${tier.popular ? "text-slate-400" : "text-[#94A3B8]"}`}>{t("pricing.lifetime")}</span>}
                </div>
                <ul className="space-y-3 flex-1">
                  {t(`pricing.${tier.k}`).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#FF5500]" />
                      <span className={tier.popular ? "text-slate-200" : "text-[#475569]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => checkout(tier)} data-testid={`pricing-${tier.name.split(" ")[0].toLowerCase()}-button`}
                  className={`mt-7 rounded-2xl py-3 font-semibold transition-colors ${tier.popular ? "bg-[#FF5500] hover:bg-[#E04800] text-white" : tier.lookup ? "bg-[#0F172A] text-white hover:bg-[#FF5500]" : "border border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                  {tier.lookup ? t("pricing.choose") : t("pricing.startFree")}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INSTALL / DESKTOP */}
      <section id="install" className="max-w-6xl mx-auto px-4 sm:px-8 pb-8">
        <Reveal>
          <div className="rounded-[2rem] border border-[#E2E8F0] bg-white p-8 sm:p-12">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-[#FF5500]">{t("install.tag")}</span>
                <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">{t("install.title")}</h2>
                <p className="text-[#475569] mt-3 max-w-xl">{t("install.subtitle")}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <a href={DESKTOP_WIN_URL} download data-testid="desktop-download-windows"
                className="group rounded-2xl border border-[#E2E8F0] hover:border-[#0F172A] p-6 flex flex-col gap-3 transition-colors">
                <Monitor className="h-7 w-7 text-[#0F172A]" />
                <div>
                  <div className="font-heading text-lg font-bold">{t("install.winTitle")}</div>
                  <div className="text-sm text-[#475569]">{t("install.winSub")}</div>
                </div>
                <span className="mt-auto inline-flex items-center gap-2 font-semibold text-[#FF5500]">{t("install.dl")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </a>
              <button onClick={() => { toast.info(t("install.macHint"), { duration: 8000 }); navigate("/studio"); }} data-testid="desktop-mac-button"
                className="group rounded-2xl border border-[#E2E8F0] hover:border-[#0F172A] p-6 flex flex-col gap-3 text-left transition-colors">
                <Apple className="h-7 w-7 text-[#0F172A]" />
                <div>
                  <div className="font-heading text-lg font-bold">{t("install.macTitle")}</div>
                  <div className="text-sm text-[#475569]">{t("install.macSoon")}</div>
                </div>
                <span className="mt-auto inline-flex items-center gap-2 font-semibold text-[#FF5500]">{t("install.macInstall")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </button>
              <button onClick={() => navigate("/studio")} data-testid="desktop-pwa-button"
                className="group rounded-2xl border border-[#E2E8F0] hover:border-[#0F172A] p-6 flex flex-col gap-3 text-left transition-colors">
                <Chrome className="h-7 w-7 text-[#0F172A]" />
                <div>
                  <div className="font-heading text-lg font-bold">{t("install.pwaTitle")}</div>
                  <div className="text-sm text-[#475569]">{t("install.pwaSub")}</div>
                </div>
                <span className="mt-auto inline-flex items-center gap-2 font-semibold text-[#FF5500]">{t("install.pwaOpen")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="px-4 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#0B0D12] text-white p-10 sm:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 15% 20%, #FF5500, transparent 40%), radial-gradient(circle at 85% 80%, #6366F1, transparent 42%)" }} />
            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight">{t("waitlist.title")}</h2>
                <p className="text-slate-300 mt-4">{t("waitlist.desc")}</p>
              </div>
              <form onSubmit={joinWaitlist} className="flex flex-col sm:flex-row gap-3" data-testid="waitlist-form">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="waitlist-email-input" placeholder={t("waitlist.placeholder")}
                  className="flex-1 bg-white/10 border border-white/20 rounded-2xl px-5 py-4 outline-none focus:border-[#FF5500] transition-colors placeholder:text-slate-400" />
                <button disabled={busy} data-testid="waitlist-submit-button"
                  className="bg-[#FF5500] hover:bg-[#E04800] rounded-2xl px-7 py-4 font-semibold transition-colors disabled:opacity-50 whitespace-nowrap">
                  {busy ? "..." : t("waitlist.submit")}
                </button>
              </form>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo className="h-8 w-8" withText textClass="text-lg" />
          <div className="flex gap-6 text-sm text-[#475569] font-mono">
            <span>{t("footer.b1")}</span><span>{t("footer.b2")}</span><span>{t("footer.b3")}</span>
          </div>
          <span className="text-xs text-[#94A3B8]">{t("footer.copy")}</span>
        </div>
      </footer>
      <AiChatWidget mode="support" />
    </div>
  );
}
